import { Button, DialogContent, DialogActions, TextField, CircularProgress, Alert, Box, Typography } from "@mui/material";
import { useStore } from "../../store/store";
import axios from "axios";
import { useForm, UseFormReturn, Controller } from "react-hook-form";
import { useState, useEffect } from "react";
import { useRelatedRecords } from "../../api/useRelatedRecords";
import { ServiceProvided, TreatmentPrescription } from "../../screens/treatments/treatment-prescription/types";
import { activeServicesProvidedFilter, displayEstimatedSessions, plannedServicesProvidedFilter } from "../../screens/treatments/treatment-prescription/utils";
import { EEG_SERVICES, NFB_SERVICE, PSY_SERVICE, FOLLOWUP_SERVICE, MAKE_WEBHOOKS } from "../../utils/constants";
import { ClientAndRelativesEmailSelectionField } from "../fieldsInputs/fields/customized/ClientAndRelativesEmailSelectionField";
import { capitalizeFirstLetter } from "../../utils/textUtils";
import { getClientTypeFromClientNumber } from "../../utils/getClientAge";

type NamesPreviewProps = {
	clientName: string;
	receiverName?: string;
};

const NamesPreview = ({ clientName, receiverName }: NamesPreviewProps) => (
	<Box sx={{ display: "flex", gap: 4, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
		<Box>
			<Typography variant="caption" color="text.secondary">Nome do Cliente</Typography>
			<Typography variant="body2" fontWeight={500}>{clientName || "—"}</Typography>
		</Box>
		{receiverName !== undefined && (
			<Box>
				<Typography variant="caption" color="text.secondary">Nome do Responsável</Typography>
				<Typography variant="body2" fontWeight={500}>{receiverName || "—"}</Typography>
			</Box>
		)}
	</Box>
);

const defaultTreatmentPrescription = `Durante a sua avaliação inicial, foi prescrito o seguinte plano de tratamento:
- X sessões de Neurofeedback, realizadas duas vezes por semana.
- X sessões de Psicologia, realizadas uma vez por semana.
- X Reuniões de Acompanhamento, realizadas uma vez por mês.
- 2 qEEG (Eletroencefalograma quantitativo): um após a vigésima sessão de Neurofeedback e outro no final do tratamento.

*Salienta-se e salvaguarda-se que o número de sessões indicado é uma estimativa e pode sofrer alterações consoante a evolução do tratamento.
`;


type TreatmentAndPaymentInformationProps = {
	context: any;
	agreement: boolean;
	onClose: () => void;
};

export const TreatmentAndPaymentInformation = ({ context, agreement, onClose }: TreatmentAndPaymentInformationProps) => {
	const { notifications } = useStore();
	const [isLoading, setIsLoading] = useState(false);
	const client = context?.item?.client;
	const isAdult = client?.client_id ? getClientTypeFromClientNumber(client.client_id) === "adult" : false;
	const responsibleForTreatment = context?.item?.relative_resposible_for_treatment?.[0]?.relatives_id;
	const responsibleName = capitalizeFirstLetter(responsibleForTreatment?.Nome?.trim() || "");
	const clientFirstName = capitalizeFirstLetter(client?.user_id?.first_name?.trim() || "");
	const receiverName = isAdult ? clientFirstName : responsibleName;

	const { data: relatives } = useRelatedRecords("relatives", "*.*", "client_id", client?.id);
	const { data: treatmentPrescriptions } = useRelatedRecords(
		"treatment_prescription",
		treatmentPrescriptionFieldsQuery,
		"treatment",
		context?.item?.id
	);
	const treatmentPrescription = makeTreatmentPrescription(treatmentPrescriptions?.items, defaultTreatmentPrescription);
	const prescriptionWarning = getPrescriptionWarning(treatmentPrescriptions?.items);

	const clientData = {
		name: `${client?.user_id?.first_name} ${client?.user_id?.last_name}`,
		email: client?.user_id?.email,
	};
	const relativesData = relatives?.items?.map((relative: { Nome: string; email: string }) => ({
		name: relative?.Nome || "",
		email: relative?.email || "",
	}));

	const entityForm = useForm({
		defaultValues: {
			treatment_prescription: treatmentPrescription,
			emailData: {
				id: client ? `${client.user_id.email}-${client.user_id.first_name} ${client.user_id.last_name}` : "",
				name: client?.user_id?.first_name || "",
				email: client?.user_id?.email || "",
			},
		},
	});

	useEffect(() => {
		if (treatmentPrescription) {
			entityForm.setValue("treatment_prescription", treatmentPrescription);
		}
	}, [treatmentPrescription]);

	const handleSend = async (formData: { treatment_prescription: string; emailData: { id: string; name: string; email: string } }) => {
		if (!formData.treatment_prescription) {
			alert("Preencha todos os campos");
			return;
		}
		if (formData.treatment_prescription.includes("X sessões")) {
			notifications.setNotification({
				isOpen: true,
				message: "Substitua os campos 'X sessões' antes de enviar.",
				severity: "warning",
			});
			return;
		}
		notifications.setNotification({
			isOpen: false,
		});
		setIsLoading(true);

		const treatment = context.item;
		const gender = treatment.client?.gender;

		if (!clientFirstName) {
			notifications.setNotification({ isOpen: true, message: "Não foi possível enviar: nome do cliente em falta.", severity: "error" });
			setIsLoading(false);
			return;
		}
		if (!isAdult && !receiverName) {
			notifications.setNotification({ isOpen: true, message: "Não foi possível enviar: dados do responsável em falta no tratamento.", severity: "error" });
			setIsLoading(false);
			return;
		}

		try {
			await axios({
				method: "POST",
				url: MAKE_WEBHOOKS.send_payment_info_emails,
				data: {
					agreement: agreement,
					treatmentId: treatment.id,
					client_id: treatment.client.client_id,
					treatmentPrescription: formData.treatment_prescription,
					clientName: clientFirstName,
					email: formData.emailData.email,
					receiverName: receiverName,
					genderPronoun: gender === "female" ? "da" : gender === "male" ? "do" : "do(a)"
				},
			});

			notifications.setNotification({
				isOpen: true,
				message: `Informação de tratamento e pagamento enviada com sucesso para email ${formData.emailData.email}`,
				severity: "success",
			});

			onClose();
			entityForm.reset();
		} catch (error: any) {
			const errorMessage = error?.response?.data?.errorMessage || "Erro ao enviar link de pagamento";
			notifications.setNotification({
				isOpen: true,
				message: errorMessage,
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<form onSubmit={entityForm.handleSubmit(handleSend)}>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				{prescriptionWarning && (
					<Alert severity="warning" sx={{ alignItems: "center" }}>{prescriptionWarning}</Alert>
				)}
				<NamesPreview
					clientName={clientFirstName}
					receiverName={isAdult ? undefined : receiverName}
				/>
				<Controller
					name="treatment_prescription"
					control={entityForm.control}
					render={({ field }) => (
						<TextField
							{...field}
							autoFocus
							margin="dense"
							label="Descrição"
							type="text"
							fullWidth
							multiline
							rows={9}
						/>
					)}
				/>
				<ClientAndRelativesEmailSelectionField
					entityForm={entityForm as unknown as UseFormReturn}
					client={clientData}
					relatives={relativesData}
				/>
			</DialogContent>
			<DialogActions>
				<Button color="secondary" onClick={onClose}>
					Cancelar
				</Button>
				<Button type="submit">{isLoading ? <CircularProgress color="inherit" size={24} /> : "Enviar"}</Button>
			</DialogActions>
		</form>
	);
};

const treatmentPrescriptionFieldsQuery = [
	"id, services_prescribed.id, services_prescribed.service.id, services_prescribed.service.name, services_prescribed.estimated_total_sessions",
	"services_provided.id,services_provided.date_begin,services_provided.date_end,services_provided.service.id,services_provided.service.name,services_provided.session_quantity,services_provided.session_periodicity",
].join(",");

const getPrescriptionWarning = (treatmentPrescriptions: Array<TreatmentPrescription>): string | null => {
	const existsTreatmentPrescription = Array.isArray(treatmentPrescriptions) && treatmentPrescriptions.length > 0
	const noServPrescribed = "Não existem Serviços Prescritos. O número de sessões e a periodicidade terão de ser preenchidos manualmente. A periodicidade está a mostar os valores 'default'"
	if (!existsTreatmentPrescription) return noServPrescribed
	const servicesPrescribed = treatmentPrescriptions[0].services_prescribed;
	const servicesProvided = treatmentPrescriptions[0].services_provided;
	const hasActiveOrPlanned = servicesProvided.some(activeServicesProvidedFilter) || servicesProvided.some(plannedServicesProvidedFilter);

	if (servicesPrescribed.length === 0) return noServPrescribed

	if (!hasActiveOrPlanned)
		return "Não existem frequências a decorrer ou planeadas nos Serviços Prestados, por isso a periodicidade não foi preenchida automaticamente - está a mostar os valores 'default'.";

	return null;
};

const makeTreatmentPrescription = (treatmentPrescriptions: Array<TreatmentPrescription>, defaultTreatmentPrescription: string): string => {
	if (!Array.isArray(treatmentPrescriptions) || treatmentPrescriptions.length === 0)
		return defaultTreatmentPrescription;

	const servicesPrescribed = treatmentPrescriptions[0].services_prescribed;
	const activeServicesProvided = treatmentPrescriptions[0].services_provided.filter(activeServicesProvidedFilter)
    const plannedServicesProvided = treatmentPrescriptions[0].services_provided.filter(plannedServicesProvidedFilter)
	if (servicesPrescribed.length === 0) return defaultTreatmentPrescription;

	const prescriptionText = servicesPrescribed
		.map((servPrescribed) => {
			let serviceProvided = activeServicesProvided.find((servProvided) => servProvided.service.id === servPrescribed.service.id);
            if (!serviceProvided) {
                // if no ongoing servProvided, check to see if there is a planned one and use that
                serviceProvided = plannedServicesProvided.find((servProvided) => servProvided.service.id === servPrescribed.service.id);
            }

			const periodicity = serviceProvided ? getPeriodicityText(serviceProvided) : getDefaultPeriodicityText(servPrescribed.service.id);
			const isQEEg = EEG_SERVICES.includes(servPrescribed.service.id);
			if (isQEEg) {
				return `- Reavaliações qEEG ao longo do processo, de acordo com a evolução e orientação clínica`;
			}
			return `- ${displayEstimatedSessions(servPrescribed.estimated_total_sessions, servPrescribed.service.id)} sessões de ${servPrescribed.service.name}, realizadas ${periodicity}`;
		}).join("\n");

	return prescriptionText;
};

const getPeriodicityText = (serviceProvided: ServiceProvided): string => {
	if (serviceProvided.session_quantity === 1) {
		if (serviceProvided.session_periodicity === "monthly") return "uma vez por mês";
		if (serviceProvided.session_periodicity === "weekly") return "uma vez por semana";
	}
	if (serviceProvided.session_periodicity === "monthly")
		return `${serviceProvided.session_quantity} vezes por mês`;
	if (serviceProvided.session_periodicity === "weekly")
		return `${serviceProvided.session_quantity} vezes por semana`;
    if (serviceProvided.session_periodicity === "not_applicable")
		return "em momentos a definir";
	return "";
};

const defaultPeriodicityByService: Record<number, string> = {
	[NFB_SERVICE]: "duas vezes por semana",
	[PSY_SERVICE]: "uma vez por semana",
	[FOLLOWUP_SERVICE]: "uma vez por mês",
};

const getDefaultPeriodicityText = (serviceId: number): string => {
	return defaultPeriodicityByService[serviceId] || "";
};
